import type { User, Band, VibeTag, MusicTag } from '../types/database';

interface MatchScore {
  band: Band;
  totalScore: number;
  breakdown: {
    budget: number;
    vibe: number;
    music: number;
    crowd: number;
    friends: number;
  };
  reasons: string[];
}

interface FriendMatch {
  bandId: string;
  count: number;
}

// Budget ranges in USD
const BUDGET_RANGES: Record<string, { min: number; max: number }> = {
  under_500: { min: 0, max: 500 },
  '500_800': { min: 500, max: 800 },
  '800_1200': { min: 800, max: 1200 },
  '1200_plus': { min: 1200, max: 10000 },
  unlimited: { min: 0, max: 100000 },
};

function calculateBudgetScore(user: User, band: Band): { score: number; reason?: string } {
  if (!user.budget_range || !band.price_min || !band.price_max) {
    return { score: 50 }; // Neutral if no budget info
  }

  const userBudget = BUDGET_RANGES[user.budget_range];
  if (!userBudget) return { score: 50 };

  // Check if band's price range overlaps with user's budget
  const minPrice = band.price_min;
  const maxPrice = band.price_max;

  // User can afford the cheapest option
  if (userBudget.max >= minPrice) {
    // Perfect fit - user budget covers the band's range
    if (userBudget.min <= minPrice && userBudget.max >= maxPrice) {
      return { score: 100, reason: 'Perfect budget fit' };
    }
    // User can afford some options
    if (userBudget.max >= minPrice) {
      const overlap = Math.min(userBudget.max, maxPrice) - Math.max(userBudget.min, minPrice);
      const bandRange = maxPrice - minPrice;
      const overlapPercent = bandRange > 0 ? (overlap / bandRange) * 100 : 100;
      return { score: Math.max(60, overlapPercent), reason: 'Within your budget' };
    }
  }

  // Band is above user's budget
  const overage = minPrice - userBudget.max;
  const penaltyPercent = Math.min(50, (overage / userBudget.max) * 100);
  return { score: Math.max(0, 50 - penaltyPercent) };
}

function calculateVibeScore(user: User, band: Band): { score: number; reason?: string } {
  if (!user.vibe_preferences?.length || !band.vibe_tags?.length) {
    return { score: 50 };
  }

  const userVibes = new Set(user.vibe_preferences);
  const bandVibes = new Set(band.vibe_tags);

  const matches = [...userVibes].filter((v) => bandVibes.has(v as VibeTag));
  const matchCount = matches.length;

  if (matchCount === 0) {
    return { score: 20 };
  }

  const score = Math.min(100, (matchCount / user.vibe_preferences.length) * 100);

  const vibeLabels: Record<VibeTag, string> = {
    bacchanal: 'bacchanal',
    pretty: 'pretty mas',
    music: 'music-focused',
    social: 'social vibes',
    cultural: 'cultural experience',
  };

  const matchLabels = matches.map((v) => vibeLabels[v as VibeTag]).join(' & ');

  return {
    score,
    reason: `Matches your ${matchLabels} vibe`,
  };
}

function calculateMusicScore(user: User, band: Band): { score: number; reason?: string } {
  if (!user.music_preferences?.length || !band.music_tags?.length) {
    return { score: 50 };
  }

  // "Everything" preference means user is flexible
  if (user.music_preferences.includes('everything')) {
    return { score: 80, reason: 'Great music selection' };
  }

  const userMusic = new Set(user.music_preferences);
  const bandMusic = new Set(band.music_tags);

  const matches = [...userMusic].filter((m) => bandMusic.has(m as MusicTag));
  const matchCount = matches.length;

  if (matchCount === 0) {
    return { score: 30 };
  }

  const score = Math.min(100, (matchCount / user.music_preferences.length) * 100);

  return {
    score,
    reason: `Plays your music`,
  };
}

function calculateCrowdScore(user: User, band: Band): { score: number; reason?: string } {
  if (!user.crowd_preference || user.crowd_preference === 'any') {
    return { score: 70 };
  }

  if (!band.crowd_type) {
    return { score: 50 };
  }

  if (user.crowd_preference === band.crowd_type) {
    const crowdLabels: Record<string, string> = {
      young: 'Young, energetic crowd',
      mixed: 'Diverse, welcoming crowd',
      mature: 'Mature, sophisticated crowd',
    };
    return { score: 100, reason: crowdLabels[band.crowd_type] };
  }

  // Mixed is somewhat compatible with everything
  if (band.crowd_type === 'mixed') {
    return { score: 70 };
  }

  return { score: 40 };
}

function calculateFriendScore(
  bandId: string,
  friendMatches: FriendMatch[]
): { score: number; reason?: string } {
  const match = friendMatches.find((f) => f.bandId === bandId);

  if (!match || match.count === 0) {
    return { score: 0 };
  }

  if (match.count >= 4) {
    return { score: 100, reason: `${match.count} friends matched with this band` };
  }

  if (match.count >= 2) {
    return { score: 75, reason: `${match.count} friends matched with this band` };
  }

  return { score: 50, reason: '1 friend matched with this band' };
}

export function calculateMatchScores(
  user: User,
  bands: Band[],
  friendMatches: FriendMatch[] = []
): MatchScore[] {
  const scores: MatchScore[] = bands.map((band) => {
    const budget = calculateBudgetScore(user, band);
    const vibe = calculateVibeScore(user, band);
    const music = calculateMusicScore(user, band);
    const crowd = calculateCrowdScore(user, band);
    const friends = calculateFriendScore(band.id, friendMatches);

    // Use user's weights or defaults
    const weights = {
      budget: Number(user.weight_budget) || 0.25,
      vibe: Number(user.weight_vibe) || 0.25,
      music: Number(user.weight_music) || 0.2,
      crowd: Number(user.weight_crowd) || 0.15,
      friends: Number(user.weight_friends) || 0.15,
    };

    const totalScore = Math.round(
      budget.score * weights.budget +
        vibe.score * weights.vibe +
        music.score * weights.music +
        crowd.score * weights.crowd +
        friends.score * weights.friends
    );

    // Collect top reasons
    const reasons: string[] = [];
    const reasonScores = [
      { score: budget.score, reason: budget.reason },
      { score: vibe.score, reason: vibe.reason },
      { score: music.score, reason: music.reason },
      { score: crowd.score, reason: crowd.reason },
      { score: friends.score, reason: friends.reason },
    ];

    reasonScores
      .filter((r) => r.reason && r.score >= 70)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .forEach((r) => reasons.push(r.reason!));

    return {
      band,
      totalScore,
      breakdown: {
        budget: budget.score,
        vibe: vibe.score,
        music: music.score,
        crowd: crowd.score,
        friends: friends.score,
      },
      reasons,
    };
  });

  // Sort by total score descending
  return scores.sort((a, b) => b.totalScore - a.totalScore);
}

export function getMatchPercentage(score: number): string {
  return `${Math.min(99, Math.max(1, score))}%`;
}

export function getMatchLabel(score: number): string {
  if (score >= 85) return 'Perfect Match';
  if (score >= 70) return 'Great Match';
  if (score >= 55) return 'Good Match';
  if (score >= 40) return 'Okay Match';
  return 'Low Match';
}
